import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useEditorStore } from "@/stores/editor/editor-store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Workflow, ChevronDown, Loader2, Cloud, HardDrive } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  buildValidatedWorkflowRoute,
  workflowFetch,
} from "@/services/api/workflowApi";

interface WorkflowSummary {
  id: string;
  name: string;
  status: string;
  type: string;
}

interface WorkflowApiItem {
  id?: string;
  _id?: string;
  name?: string;
  status?: string;
  type?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toWorkflowApiItem(value: unknown): WorkflowApiItem {
  if (!isRecord(value)) {
    return {};
  }

  return {
    id: typeof value.id === "string" ? value.id : undefined,
    _id: typeof value._id === "string" ? value._id : undefined,
    name: typeof value.name === "string" ? value.name : undefined,
    status: typeof value.status === "string" ? value.status : undefined,
    type: typeof value.type === "string" ? value.type : undefined,
  };
}

export const WorkflowSelector: React.FC<{ className?: string }> = ({ className }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const workflowId = useEditorStore((state) => state.workflowId);
  const enterWorkflowMode = useEditorStore((state) => state.enterWorkflowMode);

  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadWorkflows = useCallback(async () => {
    try {
      setIsLoading(true);
      const routePath = buildValidatedWorkflowRoute("workflows");
      const query = new URLSearchParams({ limit: "50" }).toString();

      const response = await workflowFetch(`${routePath}?${query}`, {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      const payloadRaw: unknown = await response.json().catch((): unknown => ({ success: false }));
      const payload = isRecord(payloadRaw) ? payloadRaw : { success: false };
      const success = payload.success === true;

      if (!response.ok || !success) return;

      const list = Array.isArray(payload.data) ? payload.data.map(toWorkflowApiItem) : [];
      setWorkflows(
        list.map((workflow, i: number) => ({
          id: workflow.id || workflow._id || `workflow-${i}`,
          name: workflow.name || "Untitled Workflow",
          status: workflow.status || "draft",
          type: workflow.type || "server",
        }))
      );
      setHasLoaded(true);
    } catch {
      // Silently fail — dropdown will show empty state
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load on first open
  useEffect(() => {
    if (isOpen && !hasLoaded) {
      loadWorkflows();
    }
  }, [isOpen, hasLoaded, loadWorkflows]);

  // Also load on mount so we can show the current workflow name
  useEffect(() => {
    if (!hasLoaded) loadWorkflows();
  }, [hasLoaded, loadWorkflows]);

  const currentWorkflow = workflows.find((w) => w.id === workflowId);
  const displayName = currentWorkflow?.name || "Select Workflow";

  const handleSelect = (wf: WorkflowSummary) => {
    if (projectId) {
      try { localStorage.setItem(`lastWorkflow:${projectId}`, wf.id); } catch {}
    }
    enterWorkflowMode(wf.id, wf.name);
    setIsOpen(false);
  };

  return (
    <TooltipProvider>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 min-w-[180px] max-w-[220px] justify-between px-2.5",
                  "transition-all duration-150 ease-out",
                  "bg-card border-border text-foreground",
                  "hover:bg-muted active:scale-[0.98]",
                  className
                )}
                aria-label={`Current workflow: ${displayName}. Click to switch workflows.`}
                aria-haspopup="menu"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <Workflow className="w-4 h-4 flex-shrink-0 text-primary" />
                  <span className="text-sm font-medium truncate max-w-[120px] text-foreground">
                    {displayName}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {currentWorkflow && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0 capitalize"
                    >
                      {currentWorkflow.status}
                    </Badge>
                  )}
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Switch workflows</p>
          </TooltipContent>
        </Tooltip>

        <DropdownMenuContent
          align="end"
          className="w-[280px] p-0 bg-card border-border rounded-md shadow-lg"
        >
          <ScrollArea className="max-h-[400px]">
            <div className="py-1">
              {isLoading ? (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </div>
              ) : workflows.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No workflows found
                </div>
              ) : (
                workflows.map((wf) => {
                  const isActive = wf.id === workflowId;
                  return (
                    <button
                      key={wf.id}
                      onClick={() => handleSelect(wf)}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 py-2 text-left",
                        "transition-colors duration-150",
                        "hover:bg-muted",
                        isActive && "bg-primary/10 border-l-[3px] border-l-primary",
                        !isActive && "border-l-[3px] border-l-transparent"
                      )}
                    >
                      {wf.type === "device" ? (
                        <HardDrive className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                      ) : (
                        <Cloud className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                      )}
                      <span
                        className={cn(
                          "text-sm truncate flex-1",
                          isActive ? "text-primary" : "text-foreground"
                        )}
                      >
                        {wf.name}
                      </span>
                      <Badge
                        variant="secondary"
                        className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0 capitalize"
                      >
                        {wf.status}
                      </Badge>
                    </button>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
};
